import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Send, Trash2, Users, Mail } from "lucide-react";
import { toast } from "sonner";

export default function NewsletterPage() {
  const [subs, setSubs] = useState([]);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/newsletter/subscribers").then((r) => setSubs(r.data));
  useEffect(() => { load(); }, []);

  const sendDigest = async () => {
    if (!window.confirm("क्या आप सभी सक्रिय सब्सक्राइबर्स को आज का Top-5 डाइजेस्ट भेजना चाहते हैं?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/newsletter/send-digest");
      toast.success(`${data.sent}/${data.total} सब्सक्राइबर्स को भेजा गया`);
    } catch(err) { toast.error(formatError(err)); }
    finally { setSending(false); }
  };

  const del = async (id) => {
    if (!window.confirm("सब्सक्राइबर हटाएँ?")) return;
    try { await api.delete(`/newsletter/subscribers/${id}`); load(); }
    catch(err) { toast.error(formatError(err)); }
  };

  const active = subs.filter(s => s.is_active).length;

  return (
    <div data-testid="newsletter-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">न्यूज़लेटर सब्सक्राइबर्स</h1>
          <p className="text-sm text-slate-500 mt-1">कुल {subs.length} · सक्रिय {active}</p>
        </div>
        <Button onClick={sendDigest} disabled={sending || active === 0} className="bg-[#EA580C] hover:bg-[#c2410c]" data-testid="send-digest-btn">
          <Send className="w-4 h-4 mr-1.5"/>{sending ? "भेजा जा रहा है…" : "Top-5 डाइजेस्ट अभी भेजें"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2"><Users className="w-4 h-4"/>कुल</div>
          <div className="text-3xl font-bold text-[#0F172A]">{subs.length}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-600 font-semibold mb-2"><Mail className="w-4 h-4"/>सक्रिय</div>
          <div className="text-3xl font-bold text-emerald-600">{active}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">इस सप्ताह नये</div>
          <div className="text-3xl font-bold text-[#1E3A8A]">{subs.filter(s => (Date.now() - new Date(s.subscribed_at).getTime()) < 7*24*3600*1000).length}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>ईमेल</TableHead><TableHead>नाम</TableHead><TableHead>तिथि</TableHead>
            <TableHead>स्थिति</TableHead><TableHead className="text-right">कार्य</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {subs.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-mono text-sm">{s.email}</TableCell>
                <TableCell>{s.name || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(s.subscribed_at).toLocaleString("hi-IN")}</TableCell>
                <TableCell><Badge variant={s.is_active ? "default" : "secondary"}>{s.is_active ? "सक्रिय" : "बंद"}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => del(s.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
            {subs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">अभी कोई सब्सक्राइबर नहीं</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
